import type { ReactNode } from 'react';
import type React from 'react';

import type { ButtonProps } from '@coursera/cds-core';

/**
 * Default dimensions for banner ad images
 */
export const DEFFAULT_IMAGE_SIZE = { width: 172, height: 240 };

/**
 * Default dimensions for logo images in banner ads
 */
export const DEFAULT_LOGO_SIZE = { width: 83, height: 34 };

/**
 * Aspect ratio calculated from default image dimensions
 */
export const aspectRatio = DEFFAULT_IMAGE_SIZE.width / DEFFAULT_IMAGE_SIZE.height;

/**
 * Standard height for banner advertisements
 */
export const bannerHeight = 240;

/**
 * Margin spacing between adjacent banner ads
 */
export const pixelMarginBetweenAds = 24;

/**
 * Height of placeholder lines used in loading states
 */
export const PLACEHOLDER_LINE_HEIGHT = 24;

/**
 * Available background color options for banner ads
 */
export const BACKGROUND_COLORS = {
  dodgerBlue: 'dodgerBlue',
  froly: 'froly',
  white: 'white',
  goldenPoppy: 'goldenPoppy',
  lightCyan: 'lightCyan',
} as const;

export type BackgroundColor = (typeof BACKGROUND_COLORS)[keyof typeof BACKGROUND_COLORS];

/**
 * Valid placement locations for banner advertisements throughout the platform
 */
export const BANNER_AD_PLACEMENTS = {
  ARTICLE: 'ARTICLE',
  BROWSE_DOMAIN: 'BROWSE_DOMAIN',
  BROWSE_HOME: 'BROWSE_HOME',
  BROWSE_SUBDOMAIN: 'BROWSE_SUBDOMAIN',
  LOGGED_IN_DASHBOARD: 'LOGGED_IN_DASHBOARD',
  MEGAMENU: 'MEGAMENU',
  WEEKLY_RECS_EMAIL: 'WEEKLY_RECS_EMAIL',
} as const;

/**
 * Configuration for the number of banner ads to display per placement location
 */
export const BANNER_AD_COUNTS_BY_PLACEMENT = {
  [BANNER_AD_PLACEMENTS.LOGGED_IN_DASHBOARD]: 2,
  [BANNER_AD_PLACEMENTS.MEGAMENU]: 2,
  [BANNER_AD_PLACEMENTS.ARTICLE]: 2,
  [BANNER_AD_PLACEMENTS.BROWSE_HOME]: 2,
  [BANNER_AD_PLACEMENTS.BROWSE_DOMAIN]: 2,
  [BANNER_AD_PLACEMENTS.BROWSE_SUBDOMAIN]: 2,
  [BANNER_AD_PLACEMENTS.WEEKLY_RECS_EMAIL]: 2,
} as const;

/**
 * Type definition for banner ad placement locations
 */
export type BannerAdPlacement = (typeof BANNER_AD_PLACEMENTS)[keyof typeof BANNER_AD_PLACEMENTS];

/**
 * Props interface for the banner ad view component
 * Contains styling and content properties for rendering a banner advertisement
 */
export type BannerAdViewProps = {
  backgroundColor: BackgroundColor;
  bodyText: string | ReactNode;
  callToActionText?: string;
  imageUrl: string;
  imageAltText?: string;
  logoUrl?: string;
  logoAltText?: string;
  shouldDisplayImageResponsively?: boolean;
  imageWidthInPixels?: number;
  partnerName?: string | JSX.Element | null;
  titleText: string;
  forceOpenInNewTab?: boolean;
};

/**
 * Extended banner ad properties including creative-specific information
 */
export type BannerAdCreative = BannerAdViewProps & {
  creativeSlug: string;
};

/**
 * Campaign configuration for banner advertisements
 * Includes tracking and placement information
 */
export type BannerAdCampaign = {
  id: string;
  internalName: string;
  callToActionUrl: string;
  creative: BannerAdCreative;
  placement: BannerAdPlacement; // the intended display place as computed by the BE
  promotedEntityId: string | null; // for eventing
};

/**
 * Complete props interface for the BannerAd component
 * Includes styling, tracking, and interaction properties
 */
export type BannerAdProps = BannerAdViewProps & {
  className?: string;
  heightInPixels?: number;
  trackingName?: string;
  callToActionUrl: string;
  trackingData?: { promotedEntityId?: string | null; placement?: BannerAdPlacement };
  // Style prop is for overriding size with Storybook
  style?: React.CSSProperties;
  ctaButtonType?: ButtonProps['variant'];
};

/**
 * Props interface for the BannerAdRow component
 * Handles multiple banner ads in a row layout
 */
export type BannerAdRowProps = {
  adData: Array<BannerAdProps>;
  className?: string;
  heightInPixels?: number;
  onRefUpdated?: (x0?: HTMLElement | null) => void;
  placement?: BannerAdPlacement;
  loading?: boolean;
};
